import {
  Card,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography
} from "@mui/material";
import { ProgressButton, DialogRoot } from "@qubit/autoparts";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { HoldType } from "~/lib/helpers";
import {
  mixpanelTrack,
  mixpanelTrackModalDismiss,
  useMixpanelModalName
} from "~/lib/mixpanel-tracking";
import { InventoryHoldReasonCode } from "~/redux/public/inventory.openApi";

type SelectorModalProps = {
  open: boolean;
  maxWidth?: "xs" | "sm" | "md" | "lg";
  closeModalCallback: () => void;
  options: HoldType[];
  confirmCb: (
    holdType: HoldType,
    selectedCompartment?: string
  ) => Promise<void> | void;
  confirmButtonText: string;
  currentHold?: string;
  confirmActionTypeForTracking?: "Set Hold" | "Remove Hold";
};

/** Modal to add or remove Inventory Holds */
export function InventoryHoldModal(props: SelectorModalProps) {
  const {
    open,
    maxWidth,
    closeModalCallback,
    options,
    confirmCb,
    confirmButtonText,
    currentHold,
    confirmActionTypeForTracking
  } = props;
  const { t } = useTranslation();
  const trackedModalName = useMixpanelModalName("Inventory Hold");

  const [holdText, setHoldText] = useState<string | null>(null);
  const [holdReasonCode, setHoldReasonCode] = useState<string | null>(null);

  const [isSetHoldButtonDisabled, setIsSetHoldButtonDisabled] = useState(false);

  useEffect(() => {
    if (!holdText || !holdReasonCode) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-chain-state-updates
      setIsSetHoldButtonDisabled(true);
    } else {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-chain-state-updates
      setIsSetHoldButtonDisabled(false);
    }
  }, [holdText, holdReasonCode, setIsSetHoldButtonDisabled]);

  useEffect(() => {
    // reset hold type when modal is closed
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (!open) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setHoldText(null);
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setHoldReasonCode(null);
    }
  }, [open]);

  const getDropdownValue = () => {
    // Only return a value if there are options to avoid a MUI warning
    if (options?.length) {
      return holdText || currentHold || "";
    } else {
      return "";
    }
  };

  return (
    <DialogRoot
      open={open}
      onClose={(_e, reason) => {
        mixpanelTrackModalDismiss(trackedModalName, reason);
        closeModalCallback();
      }}
      maxWidth={maxWidth}
    >
      <Card>
        <DialogTitle>{t("bin and inventory problems")}</DialogTitle>
        <DialogContent
          sx={{
            pt: "32px !important",
            overflowY: "hidden",
            display: "flex",
            flexDirection: "column",
            gap: 4,
            alignItems: "center"
          }}
        >
          <TextField
            value={getDropdownValue()}
            onChange={(e) => setHoldText(e.target.value)}
            select
            label={t("hold type")}
            variant="outlined"
            style={{ width: "70%" }}
            slotProps={{
              htmlInput: {
                sx: { fontSize: 30 }
              }
            }}
          >
            {options.map((option) => (
              <MenuItem
                onClick={(e) => {
                  const liElement = (e.target as Element).closest("li");
                  const reasonCode = liElement?.getAttribute("data-reasoncode");

                  if (reasonCode || holdText) {
                    mixpanelTrack({
                      trackedPageName: trackedModalName,
                      label: "Select Hold Type",
                      type: "Select Option",
                      eventProperties: {
                        "Hold Type": reasonCode || holdText
                      }
                    });
                  }
                  setHoldReasonCode(reasonCode || holdText);
                }}
                data-reasoncode={option.reasonCode}
                key={option.reasonCode.toLocaleLowerCase()}
                value={option.reasonCode.toLocaleLowerCase()}
                style={{ padding: "1.5em 0 1.5em 0.5em" }}
              >
                <Typography>{t(option.reasonCode)}</Typography>
              </MenuItem>
            ))}
          </TextField>
          <ProgressButton
            buttonSize="large"
            emphasis="high"
            responsive
            variant="contained"
            color="primary"
            onClick={async () => {
              if (holdText && holdReasonCode) {
                mixpanelTrack({
                  trackedPageName: trackedModalName,
                  label: confirmActionTypeForTracking ?? "Set Hold",
                  type: "Button Click",
                  eventProperties: {
                    "Hold Type": holdReasonCode
                  }
                });
                await confirmCb({
                  reasonCode: holdReasonCode as InventoryHoldReasonCode,
                  source: "internal"
                });
              }
            }}
            fullWidth
            disabled={isSetHoldButtonDisabled}
            style={{
              alignSelf: "flex-end"
            }}
          >
            {confirmButtonText}
          </ProgressButton>
        </DialogContent>
      </Card>
    </DialogRoot>
  );
}
